import { supabase } from './supabase.js'

// ─── Public RPC wrappers ─────────────────────────────────────────────────────

export async function loadUsageInfo() {
  const { data, error } = await supabase.rpc('get_usage_info')
  if (error) throw error
  return Array.isArray(data) ? (data[0] || null) : (data || null)
}

export async function startInterviewSession({ jobDescription, interviewer } = {}) {
  const { data, error } = await supabase.rpc('start_interview')
  if (error) {
    const msg = String(error.message || '')
    if (msg.includes('not_authenticated'))     throw new Error('NOT_AUTHENTICATED')
    if (msg.includes('free_limit_reached'))    throw new Error('FREE_LIMIT_REACHED')
    if (msg.includes('monthly_limit_reached')) throw new Error('MONTHLY_LIMIT_REACHED')
    if (msg.includes('plan_unavailable'))      throw new Error('PLAN_UNAVAILABLE')
    throw new Error(msg || 'Could not start interview')
  }
  // Persist optional metadata (JD + which interviewer persona) on the brand-new
  // session row so they show up in history later. Best-effort: failures here
  // do not abort the interview — the in-memory system prompt is what drives
  // the live session.
  const sessionId = typeof data === 'string' ? data : data?.id || data
  const cleanJd = (jobDescription || '').trim()
  const cleanInt = (interviewer || '').trim()
  if (sessionId && (cleanJd || cleanInt)) {
    try {
      await supabase.rpc('set_session_metadata', {
        p_session_id: sessionId,
        p_job_description: cleanJd ? cleanJd.slice(0, 5000) : null,
        p_interviewer: cleanInt || null,
      })
    } catch (e) {
      // Non-fatal — log and continue.
      console.warn('set_session_metadata failed:', e?.message || e)
    }
  }
  return data
}

export async function endInterviewSession({
  sessionId,
  durationSeconds,
  endedReason,
  overallScore,
  feedback,
  transcript,
  dimensionScores,
  fillerWordCount,
  role,
  experience,
  tone,
  modelUsed,
}) {
  if (!sessionId) return null
  const { data, error } = await supabase.rpc('end_interview', {
    p_session_id: sessionId,
    p_duration_seconds: durationSeconds ?? 0,
    p_ended_reason: endedReason ?? 'user_ended',
    p_overall_score: overallScore ?? null,
    p_feedback: feedback ?? null,
    p_transcript: transcript ?? null,
    p_dimension_scores: dimensionScores ?? null,
    p_filler_word_count: fillerWordCount ?? null,
    p_role: role ?? null,
    p_experience: experience ?? null,
    p_tone: tone ?? null,
    p_model_used: modelUsed ?? null,
  })
  if (error) throw error
  return data
}

// ─── Helpers for the UI ──────────────────────────────────────────────────────

// Maps the 12 dimension display names produced by the evaluator into the
// snake_case keys expected by the end_interview RPC.
const DIMENSION_KEY_MAP = {
  'verbal clarity': 'verbal_clarity',
  'answer relevance': 'answer_relevance',
  'conciseness': 'conciseness',
  'critical thinking': 'critical_thinking',
  'star structure': 'star_structure',
  'examples and metrics': 'examples_metrics',
  'examples & metrics': 'examples_metrics',
  'confidence and composure': 'confidence_composure',
  'confidence & composure': 'confidence_composure',
  'enthusiasm and motivation': 'enthusiasm',
  'enthusiasm & motivation': 'enthusiasm',
  'enthusiasm': 'enthusiasm',
  'professional language': 'professional_language',
  'technical depth': 'technical_depth',
  'role-specific knowledge': 'role_specific',
  'role specific knowledge': 'role_specific',
  'situational judgement': 'situational_judgement',
  'situational judgment': 'situational_judgement',
}

export function extractDimensionScores(evaluation) {
  if (!evaluation) return {}
  const out = {}
  const categories = Array.isArray(evaluation.categories) ? evaluation.categories : []
  for (const cat of categories) {
    const dims = Array.isArray(cat.dimensions) ? cat.dimensions : []
    for (const d of dims) {
      const key = DIMENSION_KEY_MAP[String(d?.name || '').toLowerCase().trim()]
      if (!key) continue
      const n = typeof d.score === 'number' ? d.score : parseFloat(d.score)
      if (isFinite(n)) out[key] = n
    }
  }
  // Fallback: some evaluators emit a flat `breakdown` array
  if (Object.keys(out).length === 0 && Array.isArray(evaluation.breakdown)) {
    for (const b of evaluation.breakdown) {
      const key = DIMENSION_KEY_MAP[String(b?.name || '').toLowerCase().trim()]
      if (!key) continue
      const n = typeof b.score === 'number' ? b.score : parseFloat(b.score)
      if (isFinite(n)) out[key] = n
    }
  }
  return Object.keys(out).length > 0 ? out : null
}

// Returns a friendly description of the user's current allowance.
// Returns null if the usage info isn't loaded yet.
//
// Lifecycle fields (subStatus, subRenewsAt, subEndsAt, passEndsAt) come
// from get_usage_info() v3+ (May 2026) and feed the cockpit's
// cancellation / pass-expiry banner. Pre-v3 backends return undefined
// for these — consumers must treat them as optional.
export function describeUsage(usage) {
  if (!usage) return null
  const lifecycle = {
    subStatus: usage.sub_status || null,
    subRenewsAt: usage.sub_renews_at || null,
    subEndsAt: usage.sub_ends_at || null,
    passEndsAt: usage.pass_ends_at || null,
  }
  // Lifetime cap (Free tier)
  if (usage.lifetime_interviews != null) {
    const used = Number(usage.used_lifetime ?? 0)
    const total = Number(usage.lifetime_interviews)
    return {
      type: 'lifetime',
      planLabel: usage.display_name || 'Free',
      used,
      total,
      remaining: Math.max(0, total - used),
      sessionMinutes: Number(usage.session_minutes || 15),
      label: total > 0
        ? `${Math.min(used, total)} of ${total} free interviews used`
        : 'No interviews available',
      atLimit: used >= total,
      ...lifecycle,
    }
  }
  // Monthly cap (paid tiers)
  if (usage.monthly_interviews != null) {
    const used = Number(usage.used_this_period ?? 0)
    const monthly = Number(usage.monthly_interviews ?? 0)
    const carried = Number(usage.carried_forward_credits ?? 0)
    const total = monthly + carried
    return {
      type: 'monthly',
      planLabel: usage.display_name || 'Plan',
      used,
      total,
      remaining: Math.max(0, total - used),
      sessionMinutes: Number(usage.session_minutes || 30),
      label: carried > 0
        ? `${used} of ${total} interviews used this month (${carried} rolled over)`
        : `${used} of ${total} interviews used this month`,
      atLimit: used >= total,
      ...lifecycle,
    }
  }
  return null
}
